import {useDispatch} from 'react-redux';
import ICON_SETTING_LIGHT from '../../../public/static/viewer/icon-setting-light.svg';
import ICON_SETTING from '../../../public/static/viewer/icon-setting.svg';
import ICON_SOUND_OFF_LIGHT from '../../../public/static/viewer/icon-sound-off-light.svg';
import ICON_SOUND_OFF from '../../../public/static/viewer/icon-sound-off.svg';
import ICON_SOUND_ON_LIGHT from '../../../public/static/viewer/icon-sound-on-light.svg';
import ICON_SOUND_ON from '../../../public/static/viewer/icon-sound-on.svg';
import Button from '../../common/component/Button';
import Header from '../../common/component/Header';
import HeaderGradient from '../../common/component/HeaderGradient';
import Text from '../../common/component/Text';
import {REGION} from '../../common/constant';
import {Page} from '../../common/constant/page';
import useTheme from '../../common/hook/useTheme';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {ITheme} from '../../common/type';
import {routeBack} from '../../common/util/PageRouter';
import {sendViewerEvent} from '../common';
import useViewerAudio from '../hook/useViewerAudio';
import {actions} from '../state/actions';
import {IViewerState, IViewType} from '../type';

interface IProps {
  title?: string;
  ageLimit?: number;
  contentId?: number;
  episodeId: number;
  viewType: IViewType;
  prevPage: Page;
}

export default function ViewerHeader({
  title = '',
  ageLimit = 0,
  contentId,
  episodeId,
  viewType,
  prevPage,
}: IProps) {
  const dispatch = useDispatch();
  const {theme} = useTheme();
  const [viewerState, showBgm, isPlayBgm, bgm] = useTypedSelector(
    ({viewer}) => [
      viewer.viewerState,
      !!viewer.episode?.bgm,
      viewer.isPlayBgm,
      viewer.episode?.bgm,
    ]
  );

  const customProps = {
    content_id: contentId,
    episode_id: episodeId,
    viewer_type: viewType,
    event_layer: 'header',
  };

  const {play, pause} = useViewerAudio(bgm);
  const onClickSound = () => {
    dispatch(actions.setValue('isPlayBgm', !isPlayBgm));
    if (!isPlayBgm) {
      play();
    } else {
      pause();
    }
    sendViewerEvent({
      actionName: 'sound',
      action: 'click',
      customProps: {
        ...customProps,
        set_value: !isPlayBgm ? 'Y' : 'N',
      },
    });
  };

  const onClickSetting = () => {
    dispatch(actions.setViewerState('setting'));
    sendViewerEvent({
      actionName: 'viewer_setting',
      action: 'click',
      customProps,
    });
  };

  const headerState = getHeaderState(viewerState);
  const animStyle = cn('transform duration-300 transition-all', {
    'opacity-100 translate-y-0': headerState === 'appear',
    'opacity-0 delay-[350]': headerState === 'invisible',
    'opacity-0 translate-y-[-100%]': headerState === 'disappear',
  });

  return (
    <div onClick={e => e.stopPropagation()}>
      <HeaderGradient
        className={cn('h-44 pointer-events-none bg-background', animStyle)}
        isCustom
      />
      <Header
        className={animStyle}
        title={title}
        titleTextClassName="s16-semibold-white"
        rightElement={
          <>
            {showBgm && (
              <Button className="mr-23" onClick={onClickSound}>
                <img
                  src={ICON_MAP[theme][isPlayBgm ? 'on' : 'off']}
                  alt="sound"
                />
              </Button>
            )}
            <Button onClick={onClickSetting}>
              <img
                src={theme === 'light' ? ICON_SETTING_LIGHT : ICON_SETTING}
                alt="setting"
              />
            </Button>
          </>
        }
        buttonIcon="back"
        onClick={() => {
          sendViewerEvent({
            actionName: 'back',
            action: 'click',
            customProps,
          });
          routeBack();
        }}
      />
      {REGION === 'kor' &&
        ageLimit === 15 &&
        prevPage !== Page.Viewer &&
        prevPage !== Page.ContentPurchase && (
          <div
            className={cn(
              'top-64 z-navigationBar fixed flex-center w-full',
              animStyle,
              {
                '!translate-y-[-400%]': headerState === 'disappear',
              }
            )}
          >
            <Text
              className={cn(
                's10-extrabold-white rounded-9 px-8 pt-4 pb-3',
                'bg-black/20 tracking-[-0.4px]'
              )}
            >
              15세 이상 감상
            </Text>
          </div>
        )}
    </div>
  );
}

function getHeaderState(state: IViewerState) {
  if (state === 'setting') {
    return 'invisible';
  } else if (state === 'default') {
    return 'appear';
  }
  return 'disappear';
}

const ICON_MAP: {[theme in ITheme]: {on: string; off: string}} = {
  dark: {
    on: ICON_SOUND_ON,
    off: ICON_SOUND_OFF,
  },
  light: {
    on: ICON_SOUND_ON_LIGHT,
    off: ICON_SOUND_OFF_LIGHT,
  },
};
